import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase, TABLES } from '../api/db';
import { subscribeToUserConnections } from '../api/connections';
import { Brain, Clock, User, AlertCircle, CheckCircle, XCircle, Loader, Edit, BookOpen, Trash2, Eye, CheckCircle2, Lock, MessageCircle, X, Search, ExternalLink, Layers, PlusCircle, Copy, Check, Fingerprint } from 'lucide-react';
import { TASK_UNIVERSES } from '../constants/taskCreation';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Task, AIAnalysis, ConnectionRequest, TaskChat } from '../types';
import { acceptConnectionRequest, rejectConnectionRequest } from '../api/connections';
import { getUserChats, getUnreadCount } from '../api/chat';
import { getTaskerProfileByUserId } from '../api/taskerProfiles';
import TaskChatComponent from '../components/TaskChat';
import SwipeableCard from '../components/SwipeableCard';
import { normalizeAIAnalysisRow, normalizeTaskRow } from '../utils/taskRecords';


interface TaskWithAnalysis extends Task {
  aiAnalysis?: AIAnalysis;
  hasAiAnalysis?: boolean;
  difficulty?: string;
  taskAge?: string;
  taskCode?: string;
  /** True when row comes from `tasks` (marketplace / seed), not Task Hatch submissions */
  fromTasksCollection?: boolean;
}

async function attachLatestAnalyses(baseTasks: TaskWithAnalysis[]): Promise<TaskWithAnalysis[]> {
  const taskIds = Array.from(new Set(baseTasks.map((task) => task.id).filter(Boolean)));
  if (taskIds.length === 0) return baseTasks;

  try {
    const { data: analysisRows, error } = await supabase
      .from(TABLES.AI_ANALYSES)
      .select('*')
      .in('task_id', taskIds)
      .order('created_at', { ascending: false });
    if (error) throw error;

    const latestByTaskId = new Map<string, AIAnalysis>();
    (analysisRows ?? []).forEach((row) => {
      const analysis = normalizeAIAnalysisRow(row as Record<string, unknown>);
      if (!latestByTaskId.has(analysis.taskId)) {
        latestByTaskId.set(analysis.taskId, analysis);
      }
    });

    return baseTasks.map((task) => {
      const analysis = latestByTaskId.get(task.id);
      return analysis
        ? { ...task, aiAnalysis: analysis, hasAiAnalysis: true }
        : { ...task, hasAiAnalysis: false };
    });
  } catch (error) {
    console.error('Error loading task analyses:', error);
    return baseTasks.map((task) => ({ ...task, hasAiAnalysis: false }));
  }
}

const TaskDashboard = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const editTaskId = searchParams.get('edit');
  const filterParam = searchParams.get('filter') as 'all' | 'my-tasks' | 'connected' | null;
  
  const [tasks, setTasks] = useState<TaskWithAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'my-tasks' | 'connected'>(filterParam || 'all');
  const [activeTab, setActiveTab] = useState<'tasks' | 'connections'>('tasks');
  const [connections, setConnections] = useState<ConnectionRequest[]>([]);
  const [chats, setChats] = useState<TaskChat[]>([]);
  const [selectedChat, setSelectedChat] = useState<string | null>(null);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Connections UI State
  const [selectedConnectedUserId, setSelectedConnectedUserId] = useState<string | 'all'>('all');
  const [taskerNames, setTaskerNames] = useState<Record<string, string>>({});
  const [connectedTasksData, setConnectedTasksData] = useState<TaskWithAnalysis[]>([]);

  // TaskCode copy feedback + Find-by-ID
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [findByIdResult, setFindByIdResult] = useState<TaskWithAnalysis | null>(null);
  const [findByIdSearching, setFindByIdSearching] = useState(false);
  const [findByIdError, setFindByIdError] = useState('');

  // Real-time connection listener — updates instantly when any connection status changes
  // (e.g. task owner accepts a request → tasker sees it without refreshing)
  useEffect(() => {
    if (!currentUser) return;

    const syncConnections = async (all: ConnectionRequest[]) => {
      const unique = Array.from(new Map(all.map(r => [r.id, r])).values());
      setConnections(unique);

      // Resolve display names for all parties
      const uniqueUserIds = new Set<string>();
      unique.forEach(req => {
        if (req.taskOwnerId !== currentUser.uid) uniqueUserIds.add(req.taskOwnerId);
        if (req.taskerId !== currentUser.uid) uniqueUserIds.add(req.taskerId);
      });
      const names: Record<string, string> = {};
      await Promise.all(Array.from(uniqueUserIds).map(async (id) => {
        try {
          const profile = await getTaskerProfileByUserId(id);
          const fromProfile =
            profile?.personalInfo?.displayName?.trim() ||
            [profile?.personalInfo?.firstName, profile?.personalInfo?.lastName]
              .filter(Boolean).join(' ').trim();
          if (fromProfile) { names[id] = fromProfile; return; }
          const { data: uRow } = await supabase.from(TABLES.USERS).select('display_name, email').eq('id', id).maybeSingle();
          if (uRow) {
            const d = uRow as { display_name?: string; email?: string };
            names[id] = d.display_name?.trim() || d.email?.split('@')[0] || 'Collaborator';
          } else { names[id] = 'Collaborator'; }
        } catch { names[id] = 'Collaborator'; }
      }));
      setTaskerNames(prev => ({ ...prev, ...names }));
    };

    const unsubscribe = subscribeToUserConnections(currentUser.uid, (all) => {
      syncConnections(all);
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Sync connected tasks automatically
  useEffect(() => {
    const acceptedIds = connections
      .filter(c => c.status === 'accepted')
      .map(c => c.taskId);
    const uniqueIds = Array.from(new Set(acceptedIds));
    
    if (uniqueIds.length === 0) {
      setConnectedTasksData([]);
      return;
    }

    const loadMissing = async () => {
      const results: TaskWithAnalysis[] = [];
      await Promise.all(uniqueIds.map(async id => {
        try {
          const { data: hatchRow } = await supabase.from(TABLES.TASK_HATCH_SUBMISSIONS).select('*').eq('id', id).maybeSingle();
          if (hatchRow) {
            results.push(normalizeTaskRow(hatchRow as Record<string, unknown>) as TaskWithAnalysis);
            return;
          }
          const { data: taskRow } = await supabase.from(TABLES.TASKS).select('*').eq('id', id).maybeSingle();
          if (taskRow) {
            results.push({ ...(normalizeTaskRow(taskRow as Record<string, unknown>)), fromTasksCollection: true } as TaskWithAnalysis);
          }
        } catch (error) {
          console.warn(`Could not load task ${id} for connection.`, error);
        }
      }));
      setConnectedTasksData(await attachLatestAnalyses(results));
    };
    loadMissing();
  }, [connections]);

  const filteredTasks = useMemo(() => {
    let merged: TaskWithAnalysis[] = [];

    if (filter === 'all') {
      const map = new Map<string, TaskWithAnalysis>();
      tasks.forEach(t => map.set(t.id, t));
      connectedTasksData.forEach(t => map.set(t.id, t));
      merged = Array.from(map.values()).sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bTime - aTime;
      });
    } else if (filter === 'my-tasks') {
      merged = [...tasks];
    } else if (filter === 'connected') {
      // Task IDs with at least one accepted connection (either as owner or tasker)
      const acceptedTaskIds = new Set(
        connections.filter(c => c.status === 'accepted').map(c => c.taskId)
      );
      // Show connected tasks from the fetched pool (as tasker)
      // PLUS the user's own tasks that have accepted connections (as owner)
      const map = new Map<string, TaskWithAnalysis>();
      connectedTasksData.forEach(t => map.set(t.id, t));
      tasks.filter(t => acceptedTaskIds.has(t.id)).forEach(t => map.set(t.id, t));
      merged = Array.from(map.values()).filter(t => acceptedTaskIds.has(t.id));
    }

    // Filter by Connected User
    if (selectedConnectedUserId !== 'all') {
      const matchingTaskIds = new Set(
        connections
          .filter(c => 
            c.status === 'accepted' && 
            (c.taskerId === selectedConnectedUserId || c.taskOwnerId === selectedConnectedUserId)
          )
          .map(c => c.taskId)
      );
      merged = merged.filter(t => matchingTaskIds.has(t.id));
    }

    const q = searchQuery.trim().toLowerCase();
    if (!q) return merged;
    return merged.filter((t) => {
      const rawTags = t.tags as string[] | string | undefined;
      const tags =
        typeof rawTags === 'string'
          ? rawTags.split(',').map((s) => s.trim()).filter(Boolean)
          : Array.isArray(rawTags)
            ? rawTags
            : [];
      const blob = [t.title, t.summary, t.userName, t.userEmail, t.requirements, t.acceptanceCriteria, t.taskCode, ...tags]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return blob.includes(q);
    });
  }, [tasks, connectedTasksData, searchQuery, selectedConnectedUserId, connections, filter]);

  // Sync filter with URL param on mount
  useEffect(() => {
    if (filterParam && filterParam !== filter) {
      setFilter(filterParam);
    }
  }, [filterParam, filter]);

  // Update filter in URL when it changes
  const handleFilterChange = (newFilter: 'all' | 'my-tasks' | 'connected') => {
    setFilter(newFilter);
    const newSearchParams = new URLSearchParams(searchParams);
    if (newFilter === 'all') {
      newSearchParams.delete('filter');
    } else {
      newSearchParams.set('filter', newFilter);
    }
    setSearchParams(newSearchParams, { replace: true });
  };

  // Handle edit navigation separately
  useEffect(() => {
    if (editTaskId && currentUser) {
      const loadTaskForEdit = async () => {
        try {
          const { data: hatchRow } = await supabase.from(TABLES.TASK_HATCH_SUBMISSIONS).select('user_id').eq('id', editTaskId).maybeSingle();
          if (hatchRow && (hatchRow as { user_id?: string }).user_id === currentUser.uid) {
            navigate(`/task-hatch?edit=${editTaskId}`, { replace: false });
            return;
          }
          const { data: taskRow } = await supabase.from(TABLES.TASKS).select('user_id').eq('id', editTaskId).maybeSingle();
          if (taskRow && (taskRow as { user_id?: string }).user_id === currentUser.uid) {
            navigate(`/marketplace?task=${editTaskId}`, { replace: false });
            return;
          }
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('edit');
          setSearchParams(newSearchParams, { replace: true });
        } catch (error) {
          console.error('Error loading task for edit:', error);
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('edit');
          setSearchParams(newSearchParams, { replace: true });
        }
      };
      loadTaskForEdit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editTaskId, currentUser, navigate, searchParams, setSearchParams]);

  // Main effect for loading tasks
  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    // Don't load tasks if we're editing (will navigate away)
    if (editTaskId) {
      return;
    }

    const sortByCreated = (a: TaskWithAnalysis, b: TaskWithAnalysis) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    };

    const mergeById = (lists: TaskWithAnalysis[][]) => {
      const map = new Map<string, TaskWithAnalysis>();
      lists.flat().forEach((t) => map.set(t.id, t));
      return Array.from(map.values()).sort(sortByCreated);
    };

    // Task Hub only loads user's own tasks. Connected tasks come via connectedTasksData.
    const mineHatch: TaskWithAnalysis[] = [];
    const mineMarket: TaskWithAnalysis[] = [];
    const loadMyTasks = async () => {
      try {
        const [{ data: hatch }, { data: market }] = await Promise.all([
          supabase.from(TABLES.TASK_HATCH_SUBMISSIONS).select('*').eq('user_id', currentUser.uid),
          supabase.from(TABLES.TASKS).select('*').eq('user_id', currentUser.uid),
        ]);
        mineHatch.length = 0;
        (hatch ?? []).forEach((r) => mineHatch.push(normalizeTaskRow(r as Record<string, unknown>) as TaskWithAnalysis));
        mineMarket.length = 0;
        (market ?? []).forEach((r) => mineMarket.push({ ...(normalizeTaskRow(r as Record<string, unknown>)), fromTasksCollection: true } as TaskWithAnalysis));
        setTasks(await attachLatestAnalyses(mergeById([mineHatch, mineMarket])));
        setLoading(false);
      } catch (error) {
        console.error('Error fetching tasks:', error);
        setLoading(false);
      }
    };

    void loadMyTasks();

    const ch1 = supabase.channel(`dashboard-hatch-${currentUser.uid}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: TABLES.TASK_HATCH_SUBMISSIONS, filter: `user_id=eq.${currentUser.uid}` }, () => void loadMyTasks())
      .subscribe();
    const ch2 = supabase.channel(`dashboard-tasks-${currentUser.uid}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: TABLES.TASKS, filter: `user_id=eq.${currentUser.uid}` }, () => void loadMyTasks())
      .subscribe();

    return () => {
      void supabase.removeChannel(ch1);
      void supabase.removeChannel(ch2);
    };
  }, [currentUser, editTaskId]);

  const handleEdit = (task: TaskWithAnalysis) => {
    if (task.userId !== currentUser?.uid) return;
    if (task.fromTasksCollection) {
      navigate(`/marketplace?task=${task.id}`);
    } else {
      navigate(`/task-hatch?edit=${task.id}`);
    }
  };

  const ownerTable = (task: TaskWithAnalysis) =>
    task.fromTasksCollection ? TABLES.TASKS : TABLES.TASK_HATCH_SUBMISSIONS;

  const handleViewGuide = (task: TaskWithAnalysis) => {
    navigate(`/implementation-guides?task=${task.id}`);
  };

  const handleDelete = async (task: TaskWithAnalysis) => {
    if (!currentUser || task.userId !== currentUser.uid) {
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${task.title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const { error: delErr } = await supabase.from(ownerTable(task)).delete().eq('id', task.id);
      if (delErr) throw delErr;
      if (!task.fromTasksCollection && task.hasAiAnalysis) {
        try {
          await supabase.from(TABLES.AI_ANALYSES).delete().eq('task_id', task.id);
        } catch (err) {
          console.error('Error deleting AI analysis:', err);
        }
      }
    } catch (error: any) {
      console.error('Error deleting task:', error);
      alert('Failed to delete task: ' + (error.message || 'Unknown error'));
    }
  };

  const handleToggleVisibility = async (task: TaskWithAnalysis) => {
    if (!currentUser || task.userId !== currentUser.uid) {
      return;
    }

    const currentVisibility = task.visibility || 'private';
    const newVisibility = currentVisibility === 'public' ? 'private' : 'public';
    const action = newVisibility === 'public' ? 'make public' : 'make private';
    
    if (!window.confirm(`Are you sure you want to ${action} "${task.title}"?`)) {
      return;
    }
    
    try {
      const { error: updErr } = await supabase.from(ownerTable(task)).update({ visibility: newVisibility, updated_at: new Date().toISOString() }).eq('id', task.id);
      if (updErr) throw updErr;
    } catch (error: any) {
      console.error('Error updating task visibility:', error);
      alert('Failed to update task visibility: ' + (error.message || 'Unknown error'));
    }
  };

  const handleMarkCompleted = async (task: TaskWithAnalysis) => {
    if (!currentUser || task.userId !== currentUser.uid) {
      return;
    }

    if (task.status === 'completed') {
      // Allow unmarking as completed
      if (!window.confirm(`Mark "${task.title}" as not completed?`)) {
        return;
      }
      
      try {
        const { error: updErr } = await supabase.from(ownerTable(task)).update({ status: 'intake', updated_at: new Date().toISOString() }).eq('id', task.id);
        if (updErr) throw updErr;
      } catch (error: any) {
        console.error('Error updating task status:', error);
        alert('Failed to update task status: ' + (error.message || 'Unknown error'));
      }
    } else {
      // Mark as completed
      if (!window.confirm(`Mark "${task.title}" as completed?`)) {
        return;
      }
      
      try {
        const now = new Date().toISOString();
        const { error: updErr } = await supabase.from(ownerTable(task)).update({ status: 'completed', completed_at: now, updated_at: now }).eq('id', task.id);
        if (updErr) throw updErr;
      } catch (error: any) {
        console.error('Error updating task status:', error);
        alert('Failed to update task status: ' + (error.message || 'Unknown error'));
      }
    }
  };

  const handleCopyTaskCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleFindByTaskId = async (rawOverride?: string) => {
    const raw = (rawOverride ?? searchQuery).trim();
    if (!raw) return;

    setFindByIdSearching(true);
    setFindByIdError('');
    setFindByIdResult(null);

    try {
      const code = raw.toUpperCase().startsWith('GNK-') ? raw.toUpperCase() : `GNK-${raw.toUpperCase()}`;

      let found: TaskWithAnalysis | null = null;
      const { data: marketRows } = await supabase.from(TABLES.TASKS).select('*').eq('task_code', code).limit(1);
      if (marketRows && marketRows.length > 0) {
        found = { ...(normalizeTaskRow(marketRows[0] as Record<string, unknown>)), fromTasksCollection: true } as TaskWithAnalysis;
      }

      if (!found) {
        try {
          const { data: hatchRows } = await supabase.from(TABLES.TASK_HATCH_SUBMISSIONS).select('*').eq('task_code', code).limit(1);
          if (hatchRows && hatchRows.length > 0) {
            found = normalizeTaskRow(hatchRows[0] as Record<string, unknown>) as TaskWithAnalysis;
          }
        } catch {
          // Permission denied on private hatch tasks is expected
        }
      }

      if (found) {
        setFindByIdResult(found);
      } else {
        setFindByIdError('No task found. The task may be private — ask the owner to share access.');
      }
    } catch (err) {
      console.error('Error finding task by code:', err);
      setFindByIdError('Something went wrong. Please try again.');
    } finally {
      setFindByIdSearching(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-amber-500/20 text-amber-300 border-amber-500/50';
      case 'high': return 'bg-amber-500/20 text-amber-200 border-amber-500/50';
      case 'medium': return 'bg-tech-cyan/20 text-tech-cyan border-tech-cyan/50';
      case 'low': return 'bg-tech-accent/20 text-tech-accent border-tech-accent/50';
      default: return 'bg-tech-gray-light text-tech-text-muted border-tech-border';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-tech-accent" />;
      case 'in-progress':
      case 'analyzing':
      case 'ai-processing':
        return <Loader className="w-5 h-5 text-tech-cyan animate-spin" />;
      case 'cancelled':
        return <XCircle className="w-5 h-5 text-amber-400" />;
      default:
        return <Clock className="w-5 h-5 text-tech-cyan" />;
    }
  };

  const formatDate = (timestamp: string | null | undefined) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Refresh chats when the Connections tab is opened.
  // Connection state & names are kept live by the real-time onSnapshot listener above.
  const handleLoadConnections = async () => {
    if (!currentUser) return;
    setLoadingConnections(true);
    try {
      const userChats = await getUserChats(currentUser.uid);
      setChats(userChats);
    } catch (error) {
      console.error('Error loading chats:', error);
    } finally {
      setLoadingConnections(false);
    }
  };

  const handleAcceptRequest = async (requestId: string) => {
    try {
      await acceptConnectionRequest(requestId);
      // Reload connections (chat is created automatically in acceptConnectionRequest)
      await handleLoadConnections();
    } catch (error: any) {
      alert('Failed to accept request: ' + (error.message || 'Unknown error'));
    }
  };

  const handleRejectRequest = async (requestId: string) => {
    try {
      await rejectConnectionRequest(requestId);
      await handleLoadConnections();
    } catch (error: any) {
      alert('Failed to reject request: ' + (error.message || 'Unknown error'));
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-[50vh] text-tech-text flex items-center justify-center px-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-tech-cyan mx-auto mb-4" aria-hidden />
          <p className="text-tech-text-muted text-lg">Please log in to view your task dashboard.</p>
          <Link to="/login" className="inline-block mt-4 px-4 py-2 bg-tech-cyan/20 border border-tech-cyan rounded-lg text-tech-cyan hover:bg-tech-cyan/30 transition-colors">
            Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full text-tech-text px-4 py-6 md:py-8 bg-tech-mesh">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-tech-cyan/10 border border-tech-cyan/20">
              <Layers className="w-6 h-6 text-tech-cyan" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-tech-text">Task Hub</h1>
              <p className="text-xs sm:text-sm text-tech-text-muted mt-0.5">
                Your tasks, connections, and collaborations in one place
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => navigate('/task-hatch')}
              className="px-4 py-2.5 rounded-xl font-semibold text-tech-dark text-sm hover:opacity-90 transition-all flex items-center gap-2"
              style={{ background: 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)' }}
            >
              <PlusCircle className="w-4 h-4" /> New Task
            </button>
          </div>
        </div>

        {/* Main Tabs */}
        <div className="flex gap-2 mb-4 border-b border-tech-border">
          <button
            onClick={() => setActiveTab('tasks')}
            className={`px-4 py-2 font-medium transition rounded-t-lg ${
              activeTab === 'tasks'
                ? 'text-tech-cyan bg-tech-cyan/10 border-b-2 border-tech-cyan'
                : 'text-tech-text-muted hover:text-tech-text'
            }`}
          >
            Tasks
          </button>
          <button
            onClick={() => {
              setActiveTab('connections');
              handleLoadConnections();
            }}
            className={`px-4 py-2 font-medium transition rounded-t-lg ${
              activeTab === 'connections'
                ? 'text-tech-cyan bg-tech-cyan/10 border-b-2 border-tech-cyan'
                : 'text-tech-text-muted hover:text-tech-text'
            }`}
          >
            Connections
          </button>
        </div>

        {/* Unified search + filters */}
        {activeTab === 'tasks' && (
          <div className="mb-4 space-y-3">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-tech-cyan pointer-events-none" aria-hidden />
                <input
                  id="task-hub-search"
                  type="search"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setFindByIdError('');
                    setFindByIdResult(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const v = searchQuery.trim().toUpperCase();
                      if (/^(GNK-)?[A-Z]{4}-[A-Z0-9]{4}$/.test(v)) {
                        handleFindByTaskId(searchQuery);
                      }
                    }
                  }}
                  placeholder="Search tasks or paste a Task ID (GNK-XXXX-XXXX)…"
                  className="w-full pl-12 pr-4 py-3 rounded-xl bg-tech-dark/80 border border-tech-cyan/20 text-tech-text placeholder-tech-text-muted/45 focus:outline-none focus:ring-2 focus:ring-tech-cyan/30 text-sm"
                />
              </div>
              <div className="flex gap-2 overflow-x-auto hide-scrollbar shrink-0">
                {(['all', 'my-tasks', 'connected'] as const).map((filterType) => {
                  const acceptedCount = connections.filter(c => c.status === 'accepted').length;
                  const labels: Record<string, React.ReactNode> = {
                    'all': 'All',
                    'my-tasks': 'My Tasks',
                    'connected': (
                      <span className="flex items-center gap-1.5">
                        Connected
                        {acceptedCount > 0 && (
                          <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold leading-none ${
                            filter === 'connected' ? 'bg-tech-dark/30 text-tech-dark' : 'bg-tech-cyan/20 text-tech-cyan'
                          }`}>{acceptedCount}</span>
                        )}
                      </span>
                    ),
                  };
                  return (
                    <button
                      key={filterType}
                      onClick={() => handleFilterChange(filterType)}
                      className={`px-3 py-2.5 rounded-xl font-medium transition whitespace-nowrap text-sm flex-shrink-0 ${
                        filter === filterType
                          ? 'bg-tech-cyan text-tech-dark'
                          : 'bg-tech-gray/40 border border-tech-border/40 text-tech-text-muted hover:text-tech-text'
                      }`}
                    >
                      {labels[filterType]}
                    </button>
                  );
                })}
                {Object.keys(taskerNames).length > 0 && (
                  <select
                    value={selectedConnectedUserId}
                    onChange={(e) => setSelectedConnectedUserId(e.target.value)}
                    className="appearance-none bg-tech-gray/40 border border-tech-border/40 text-tech-text font-medium px-3 py-2 rounded-xl text-sm focus:outline-none cursor-pointer max-w-[150px] truncate"
                    title="Filter by collaborator"
                  >
                    <option value="all">All people</option>
                    {Object.entries(taskerNames).map(([id, name]) => (
                      <option key={id} value={id}>{name}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-tech-text-muted">
              <span>
                <strong className="text-tech-text">{filteredTasks.length}</strong>
                {searchQuery.trim() ? ` match${filteredTasks.length === 1 ? '' : 'es'}` : ` task${filteredTasks.length === 1 ? '' : 's'}`}
              </span>
              {searchQuery.trim() && (
                <button type="button" onClick={() => setSearchQuery('')} className="text-tech-cyan hover:underline">Clear</button>
              )}
            </div>

            {findByIdSearching && (
              <div className="flex items-center gap-2 text-xs text-tech-cyan">
                <Loader className="w-3 h-3 animate-spin" /> Looking up task…
              </div>
            )}
            {findByIdResult && (
              <div className="rounded-xl border border-tech-accent/30 bg-tech-accent/5 p-3 flex items-center gap-3 flex-wrap">
                <Fingerprint className="w-4 h-4 text-tech-accent shrink-0" />
                <span className="font-mono text-xs text-tech-accent font-bold tracking-wider">{findByIdResult.taskCode}</span>
                <span className="text-tech-text font-semibold text-sm truncate flex-1 min-w-0">{findByIdResult.title}</span>
                <button
                  type="button"
                  onClick={() => navigate(`/tasks/${findByIdResult!.id}`)}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-tech-dark flex items-center gap-1 shrink-0"
                  style={{ background: 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)' }}
                >
                  <ExternalLink className="w-3 h-3" /> Open
                </button>
              </div>
            )}
            {findByIdError && (
              <p className="text-xs text-amber-400 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> {findByIdError}
              </p>
            )}
          </div>
        )}

        {/* Tasks List */}
        {activeTab === 'tasks' && loading ? (
          <div className="text-center py-12">
            <Loader className="w-12 h-12 text-tech-cyan mx-auto animate-spin" aria-hidden />
            <p className="text-tech-text-muted mt-4">Loading tasks...</p>
          </div>
        ) : activeTab === 'tasks' && filter === 'connected' && filteredTasks.length === 0 && !searchQuery.trim() ? (
          <div className="text-center py-12 tech-card rounded-xl">
            <MessageCircle className="w-16 h-16 text-tech-cyan/50 mx-auto mb-4" aria-hidden />
            <p className="text-tech-text text-lg mb-2">No connected tasks yet</p>
            <p className="text-tech-text-muted text-sm mb-4">
              {connections.filter(c => c.status === 'pending').length > 0
                ? 'You have pending requests waiting for approval.'
                : 'Browse the Task Marketplace and send a connection request to get started.'}
            </p>
            <button
              onClick={() => navigate('/marketplace')}
              className="px-4 py-2 rounded-lg font-semibold text-tech-dark focus:outline-none focus:ring-2 focus:ring-tech-cyan/50"
              style={{ background: 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)' }}
            >
              Browse Marketplace
            </button>
          </div>
        ) : activeTab === 'tasks' && tasks.length === 0 ? (
          <div className="text-center py-12 tech-card rounded-xl">
            <Brain className="w-16 h-16 text-tech-cyan/50 mx-auto mb-4" aria-hidden />
            <p className="text-tech-text text-lg mb-2">No tasks yet</p>
            <p className="text-tech-text-muted text-sm mb-4">
              {filter === 'my-tasks'
                ? 'You haven\'t created any tasks yet.'
                : 'Create your first task to get started.'}
            </p>
            <button
              onClick={() => navigate('/task-hatch')}
              className="px-4 py-2 rounded-lg font-semibold text-tech-dark focus:outline-none focus:ring-2 focus:ring-tech-cyan/50"
              style={{ background: 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)' }}
            >
              Create your first task
            </button>
          </div>
        ) : activeTab === 'tasks' && filteredTasks.length === 0 && searchQuery.trim() ? (
          <div className="text-center py-12 tech-card rounded-xl">
            <Search className="w-14 h-14 text-tech-text-muted mx-auto mb-4" aria-hidden />
            <p className="text-tech-text text-lg mb-2">No tasks match your search</p>
            <p className="text-tech-text-muted text-sm mb-4">Try a different keyword or clear the search box.</p>
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="px-4 py-2 rounded-lg border border-tech-border text-tech-cyan hover:bg-tech-cyan/10"
            >
              Clear search
            </button>
          </div>
        ) : activeTab === 'tasks' ? (
          <div className="grid gap-5">
            {filteredTasks.map((task) => {
              const recentlyUpdated = task.userId !== currentUser.uid &&
                task.updatedAt &&
                (Date.now() - (task.updatedAt ? new Date(task.updatedAt).getTime() : 0)) < 5 * 60 * 1000;
              return (
              <SwipeableCard
                key={task.id}
                onSwipeRight={task.userId === currentUser.uid ? () => handleMarkCompleted(task) : undefined}
                onSwipeLeft={() => navigate(`/tasks/${task.id}`)}
                rightLabel="Done"
                leftLabel="Open"
              >
              <div
                className={`group relative overflow-hidden rounded-2xl border bg-gradient-to-br from-tech-gray/30 to-tech-dark/40 p-4 md:p-6 pl-5 md:pl-6 shadow-lg shadow-black/20 transition-all hover:border-tech-cyan/25 hover:shadow-tech-cyan/5 ${
                  recentlyUpdated ? 'border-tech-accent/50 ring-1 ring-tech-accent/20' : 'border-tech-border/90'
                }`}
              >
                {recentlyUpdated && (
                  <div className="absolute top-3 right-3 flex items-center gap-1.5 z-10">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-tech-accent opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-tech-accent" />
                    </span>
                    <span className="text-[9px] text-tech-accent font-semibold">Updated</span>
                  </div>
                )}
                {(() => {
                  const taskUniverse = TASK_UNIVERSES.find(u => u.id === task.universe);
                  return taskUniverse ? (
                    <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl" style={{ background: `linear-gradient(to bottom, ${taskUniverse.color}, ${taskUniverse.color}80)` }} aria-hidden />
                  ) : (
                    <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl bg-gradient-to-b from-tech-cyan to-blue-600 opacity-90" aria-hidden />
                  );
                })()}
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 md:gap-3 mb-2 flex-wrap">
                      {(() => {
                        const taskUniverse = TASK_UNIVERSES.find(u => u.id === task.universe);
                        return taskUniverse ? (
                          <span className="text-lg shrink-0" title={taskUniverse.label}>{taskUniverse.emoji}</span>
                        ) : getStatusIcon(task.status);
                      })()}
                      <button
                        type="button"
                        onClick={() => navigate(`/tasks/${task.id}`)}
                        className="text-left text-base md:text-lg font-semibold text-tech-text truncate flex-1 min-w-0 hover:text-tech-cyan transition-colors"
                      >
                        {task.title}
                      </button>
                      {task.userId === currentUser.uid && (
                        <span className="px-2 py-0.5 text-[10px] bg-tech-cyan/15 text-tech-cyan rounded-md border border-tech-cyan/20 shrink-0">Mine</span>
                      )}
                      {(() => {
                        const taskConnections = connections.filter(c => c.status === 'accepted' && c.taskId === task.id);
                        if (taskConnections.length === 0) return null;
                        const collaboratorNames = taskConnections.map(c => {
                          const otherId = c.taskerId === currentUser.uid ? c.taskOwnerId : c.taskerId;
                          return taskerNames[otherId] || 'Collaborator';
                        });
                        return (
                          <span
                            className="px-2 py-0.5 text-[10px] bg-tech-accent/15 text-tech-accent rounded-md border border-tech-accent/20 shrink-0 flex items-center gap-1"
                            title={collaboratorNames.join(', ')}
                          >
                            <CheckCircle className="w-2.5 h-2.5" />
                            {taskConnections.length === 1 ? collaboratorNames[0] : `${taskConnections.length} connected`}
                          </span>
                        );
                      })()}
                      <span className={`px-2 py-0.5 text-[10px] rounded-md flex items-center gap-1 shrink-0 ${
                        task.visibility === 'public'
                          ? 'bg-tech-accent/15 border border-tech-accent/30 text-tech-accent'
                          : 'bg-tech-gray-light border border-tech-border text-tech-text-muted'
                      }`}>
                        {task.visibility === 'public' ? <><Eye className="w-2.5 h-2.5" /> Public</> : <><Lock className="w-2.5 h-2.5" /> Private</>}
                      </span>
                      {task.source === '3rdeyefeel' && (
                        <span className="px-2 py-0.5 text-[10px] rounded-md border border-amber-400/30 bg-amber-500/10 text-amber-200 shrink-0">
                          via 3rd Eye Feel
                        </span>
                      )}
                    </div>
                    {task.taskCode && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleCopyTaskCode(task.taskCode!); }}
                        className="group/code inline-flex items-center gap-1.5 mb-2 px-2.5 py-1 rounded-lg bg-gradient-to-r from-tech-cyan/10 to-blue-600/10 border border-tech-cyan/25 hover:border-tech-cyan/50 transition-all cursor-pointer"
                        title="Click to copy Task ID"
                      >
                        <Fingerprint className="w-3 h-3 text-tech-cyan/70" />
                        <span className="font-mono text-[11px] font-bold tracking-widest text-tech-cyan">{task.taskCode}</span>
                        {copiedCode === task.taskCode ? (
                          <Check className="w-3 h-3 text-tech-accent" />
                        ) : (
                          <Copy className="w-3 h-3 text-tech-text-muted/40 group-hover/code:text-tech-cyan/80 transition-colors" />
                        )}
                      </button>
                    )}
                    <p className="text-tech-text-muted text-sm mb-3 line-clamp-2">{task.goal || task.summary}</p>
                  </div>
                  <div className="flex gap-2 flex-wrap md:flex-nowrap">
                    <button
                      type="button"
                      onClick={() => navigate(`/tasks/${task.id}`)}
                      className="px-3 py-2 bg-tech-cyan/15 border border-tech-cyan/40 rounded-lg text-tech-cyan hover:bg-tech-cyan/25 transition flex items-center gap-2 flex-shrink-0 text-sm font-medium"
                      title="Open task detail, creator profile, and responses"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Open
                    </button>
                    {task.userId === currentUser.uid && (
                      <>
                        {task.status !== 'completed' && (
                          <button
                            onClick={() => handleMarkCompleted(task)}
                            className="p-2 bg-tech-accent/20 border border-tech-accent/50 rounded-lg text-tech-accent hover:bg-tech-accent/30 transition flex-shrink-0"
                            title="Mark as Completed"
                          >
                            <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5" />
                          </button>
                        )}
                        {task.status === 'completed' && (
                          <button
                            onClick={() => handleMarkCompleted(task)}
                            className="p-2 bg-tech-cyan/20 border border-tech-cyan/50 rounded-lg text-tech-cyan hover:bg-tech-cyan/30 transition flex-shrink-0"
                            title="Mark as Not Completed"
                          >
                            <XCircle className="w-4 h-4 md:w-5 md:h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(task)}
                          className="p-2 bg-tech-cyan/20 border border-tech-cyan/50 rounded-lg text-tech-cyan hover:bg-tech-cyan/30 transition flex-shrink-0"
                          title="Edit Task"
                        >
                          <Edit className="w-4 h-4 md:w-5 md:h-5" />
                        </button>
                        <button
                          onClick={() => handleToggleVisibility(task)}
                          className={`p-2 border rounded-lg transition flex items-center gap-1 flex-shrink-0 ${
                            task.visibility === 'public'
                              ? 'bg-tech-accent/20 border-tech-accent/50 text-tech-accent hover:bg-tech-accent/30'
                              : 'bg-tech-gray-light border-tech-border text-tech-text-muted hover:bg-tech-gray'
                          }`}
                          title={task.visibility === 'public' ? 'Make Private' : 'Make Public'}
                        >
                          {task.visibility === 'public' ? (
                            <Eye className="w-4 h-4 md:w-5 md:h-5" />
                          ) : (
                            <Lock className="w-4 h-4 md:w-5 md:h-5" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDelete(task)}
                          className="p-2 bg-amber-500/20 border border-amber-400/50 rounded-lg text-amber-200 hover:bg-amber-500/30 transition flex-shrink-0"
                          title="Delete Task"
                        >
                          <Trash2 className="w-4 h-4 md:w-5 md:h-5" />
                        </button>
                      </>
                    )}
                    {task.hasAiAnalysis && (
                      <button
                        onClick={() => handleViewGuide(task)}
                        className="p-2 bg-tech-accent/20 border border-tech-accent/50 rounded-lg text-tech-accent hover:bg-tech-accent/30 transition flex items-center gap-1 md:gap-2 flex-shrink-0"
                        title="View Implementation Guide"
                      >
                        <BookOpen className="w-4 h-4 md:w-5 md:h-5" />
                        <span className="text-xs md:text-sm hidden sm:inline">Guide</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Status pipeline strip */}
                <div className="flex items-center gap-0.5 mb-3">
                  {(['intake', 'in-progress', 'completed'] as const).map((stage, idx) => {
                    const order = ['intake', 'analyzing', 'ai-processing', 'neural-requested', 'assigned', 'in-progress', 'completed'];
                    const taskIdx = order.indexOf(task.status);
                    const stageIdx = order.indexOf(stage);
                    const isActive = task.status === stage;
                    const isPast = taskIdx > stageIdx;
                    const isCancelled = task.status === 'cancelled';
                    return (
                      <div key={stage} className="flex items-center flex-1 gap-0.5">
                        <div className={`h-1.5 flex-1 rounded-full transition-all ${
                          isCancelled ? 'bg-amber-500/30'
                          : isPast || isActive ? 'bg-tech-cyan' 
                          : 'bg-tech-border/40'
                        }`} />
                        {idx < 2 && <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${isPast || isActive ? 'bg-tech-cyan' : 'bg-tech-border/40'}`} />}
                      </div>
                    );
                  })}
                  <span className={`text-[9px] font-semibold ml-1.5 shrink-0 ${
                    task.status === 'completed' ? 'text-tech-accent' 
                    : task.status === 'cancelled' ? 'text-amber-400'
                    : 'text-tech-text-muted'
                  }`}>
                    {task.status === 'intake' ? 'Open' : task.status === 'in-progress' ? 'Active' : task.status === 'completed' ? 'Done' : task.status}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-2 text-xs">
                  {(() => {
                    const taskUniverse = TASK_UNIVERSES.find(u => u.id === task.universe);
                    return taskUniverse ? (
                      <span className="px-2 py-0.5 rounded-md border text-[10px] font-bold"
                        style={{ borderColor: taskUniverse.color + '40', background: taskUniverse.color + '10', color: taskUniverse.color }}>
                        {taskUniverse.emoji} {taskUniverse.label}
                      </span>
                    ) : task.category ? (
                      <span className="px-2 py-0.5 rounded-md bg-tech-cyan/15 text-tech-cyan border border-tech-cyan/30 text-[10px]">
                        {task.category}
                      </span>
                    ) : null;
                  })()}
                  <span className={`px-2 py-0.5 rounded-md border text-[10px] ${getPriorityColor(task.priority)}`}>
                    {task.priority}
                  </span>
                  {((task as any).tools || []).slice(0, 3).map((t: any, i: number) => (
                    <span key={i} className="px-1.5 py-0.5 rounded bg-tech-gray text-[10px] text-tech-text-muted border border-tech-border/50">
                      {t.name}
                    </span>
                  ))}
                  {(task.tags || []).slice(0, 2).map((tag: string, idx: number) => (
                    <span key={idx} className="text-[10px] text-tech-cyan/60">#{tag}</span>
                  ))}
                  <Link
                    to={`/tasker/${task.userId}`}
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-1 text-tech-text-muted hover:text-tech-cyan truncate min-w-0 transition-colors ml-auto"
                  >
                    <User className="w-3 h-3 shrink-0" />
                    <span className="truncate">{task.userName}</span>
                  </Link>
                  <span className="text-tech-text-muted/60 text-[10px]">
                    {formatDate(task.createdAt)}
                  </span>
                </div>
              </div>
              </SwipeableCard>
              );
            })}
          </div>
        ) : (
          // Connections Tab Content
          <ConnectionsTabContent
            currentUser={currentUser}
            connections={connections}
            chats={chats}
            loadingConnections={loadingConnections}
            selectedChat={selectedChat}
            onSelectChat={setSelectedChat}
            onAcceptRequest={handleAcceptRequest}
            onRejectRequest={handleRejectRequest}
          />
        )}

        {/* Chat Panel - Side panel or modal */}
        {selectedChat && (
          <ChatModal
            selectedChat={selectedChat}
            chats={chats}
            currentUser={currentUser}
            onClose={() => setSelectedChat(null)}
          />
        )}
      </div>
    </div>
  );
};

// Connections Tab Component
interface ConnectionsTabContentProps {
  currentUser: any;
  connections: ConnectionRequest[];
  chats: TaskChat[];
  loadingConnections: boolean;
  selectedChat: string | null;
  onSelectChat: (chatId: string) => void;
  onAcceptRequest: (requestId: string) => void;
  onRejectRequest: (requestId: string) => void;
}

const ConnectionsTabContent: React.FC<ConnectionsTabContentProps> = ({
  currentUser,
  connections,
  chats,
  loadingConnections,
  selectedChat,
  onSelectChat,
  onAcceptRequest,
  onRejectRequest,
}) => {
  const [unreadCounts, setUnreadCounts] = useState<{ [key: string]: number }>({});

  useEffect(() => {
    // Load unread counts for all chats
    const loadUnreadCounts = async () => {
      const counts: { [key: string]: number } = {};
      for (const chat of chats) {
        try {
          const count = await getUnreadCount(chat.id, currentUser.uid);
          counts[chat.id] = count;
        } catch (error) {
          console.error('Error loading unread count:', error);
        }
      }
      setUnreadCounts(counts);
    };

    if (chats.length > 0) {
      loadUnreadCounts();
    }
  }, [chats, currentUser]);

  const pendingRequests = connections.filter(c => c.status === 'pending');

  if (loadingConnections) {
    return (
      <div className="text-center py-12">
        <Loader className="w-12 h-12 text-tech-cyan mx-auto animate-spin" aria-hidden />
        <p className="text-tech-text-muted mt-4">Loading connections...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Pending Requests */}
      {pendingRequests.length > 0 && (
        <div>
          <h2 className="text-xl font-bold text-tech-text mb-4">Pending Requests</h2>
          <div className="space-y-3">
            {pendingRequests.map((request) => (
              <div
                key={request.id}
                className="tech-card rounded-xl p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-tech-text mb-1">Task: {request.taskId}</h3>
                    {request.message && (
                      <p className="text-tech-text-muted text-sm mb-3">{request.message}</p>
                    )}
                    <p className="text-xs text-tech-text-muted">
                      Requested {(request.createdAt ? new Date(request.createdAt).toLocaleDateString() : '')}
                    </p>
                  </div>
                  {request.taskOwnerId === currentUser.uid && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => onAcceptRequest(request.id)}
                        className="px-4 py-2 bg-tech-accent/20 border border-tech-accent/50 rounded-lg text-tech-accent hover:bg-tech-accent/30 transition"
                      >
                        Accept
                      </button>
                      <button
                        onClick={() => onRejectRequest(request.id)}
                        className="px-4 py-2 bg-amber-500/20 border border-amber-500/50 rounded-lg text-amber-200 hover:bg-amber-500/30 transition"
                      >
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Accepted Connections / Chats */}
      <div>
        <h2 className="text-xl font-bold text-tech-text mb-4">Active Connections</h2>
        {chats.length === 0 ? (
          <div className="text-center py-12 tech-card rounded-xl">
            <MessageCircle className="w-16 h-16 text-tech-cyan/50 mx-auto mb-4" aria-hidden />
            <p className="text-tech-text text-lg">No active connections</p>
            <p className="text-tech-text-muted text-sm mt-2">
              Accept a connection request to start chatting
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {chats.map((chat) => {
              const unread = unreadCounts[chat.id] || 0;
              const lastMessagePreview = chat.lastMessage?.message.substring(0, 50) || 'No messages yet';
              
              return (
                <div
                  key={chat.id}
                  className="tech-card rounded-xl p-4 hover:border-tech-cyan/20 transition cursor-pointer"
                  onClick={() => onSelectChat(chat.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-tech-text mb-1 truncate">
                        {chat.taskTitle}
                      </h3>
                      <p className="text-tech-text-muted text-sm truncate">{lastMessagePreview}...</p>
                      <p className="text-xs text-tech-text-muted mt-1">
                        {(chat.lastActivityAt ? new Date(chat.lastActivityAt).toLocaleDateString() : '')}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      {unread > 0 && (
                        <span className="px-2 py-1 bg-tech-cyan text-tech-dark text-xs rounded-full font-semibold">
                          {unread}
                        </span>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectChat(chat.id);
                        }}
                        className="px-4 py-2 rounded-lg font-medium text-tech-dark hover:opacity-90 transition focus:outline-none focus:ring-2 focus:ring-tech-cyan/50"
                        style={{ background: 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)' }}
                      >
                        Open Chat
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Chat Modal Component
interface ChatModalProps {
  selectedChat: string;
  chats: TaskChat[];
  currentUser: any;
  onClose: () => void;
}

const ChatModal: React.FC<ChatModalProps> = ({ selectedChat, chats, currentUser, onClose }) => {
  const [otherName, setOtherName] = useState<string>('Collaborator');

  useEffect(() => {
    const loadParticipantName = async () => {
      const chat = chats.find(c => c.id === selectedChat);
      if (!chat || !currentUser) return;

      try {
        const { data: connRow } = await supabase
          .from(TABLES.CONNECTION_REQUESTS).select('*').eq('id', selectedChat).maybeSingle();
        
        if (connRow) {
          const request = connRow as { task_owner_id: string; tasker_id: string };
          const otherId = request.task_owner_id === currentUser.uid
            ? request.tasker_id
            : request.task_owner_id;
          
          try {
            const { data: uRow } = await supabase.from(TABLES.USERS).select('display_name').eq('id', otherId).maybeSingle();
            if (uRow) {
              setOtherName((uRow as { display_name?: string }).display_name || 'User');
              return;
            }
          } catch (err) {
            // Fall through to default
          }
        }
      } catch (error) {
        console.error('Error getting participant name:', error);
      }
    };

    loadParticipantName();
  }, [selectedChat, chats, currentUser]);

  return (
    <div className="fixed inset-0 z-50 bg-tech-darker/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-tech-dark rounded-2xl border border-tech-border w-full max-w-4xl h-[80vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-tech-border">
          <h3 className="text-lg font-semibold text-tech-text">Chat</h3>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-tech-text-muted hover:text-tech-cyan hover:bg-tech-gray transition-colors focus:outline-none focus:ring-2 focus:ring-tech-cyan/50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 min-h-0">
          <TaskChatComponent
            connectionId={selectedChat}
            taskId={chats.find(c => c.id === selectedChat)?.taskId || ''}
            otherParticipantName={otherName}
          />
        </div>
      </div>
    </div>
  );
};

export default TaskDashboard;
